import { useEffect, useRef, useState } from "react";
import { fmtDateTime } from "@/components/DatePicker";
import { useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, XCircle, Eraser } from "lucide-react";
import { toast } from "sonner";
import { OfferLetterhead, OfferPaper } from "@/components/OfferLetterhead";

function SignaturePad({ onChange }) {
  const ref = useRef(null);
  const drawing = useRef(false);
  const pos = (e) => { const r = ref.current.getBoundingClientRect(); const p = e.touches ? e.touches[0] : e; return { x: p.clientX - r.left, y: p.clientY - r.top }; };
  const start = (e) => { drawing.current = true; const c = ref.current.getContext("2d"); const { x, y } = pos(e); c.beginPath(); c.moveTo(x, y); };
  const move = (e) => { if (!drawing.current) return; e.preventDefault(); const c = ref.current.getContext("2d"); c.lineWidth = 2.2; c.lineCap = "round"; c.strokeStyle = "#0f172a"; const { x, y } = pos(e); c.lineTo(x, y); c.stroke(); };
  const end = () => { if (!drawing.current) return; drawing.current = false; onChange(ref.current.toDataURL("image/png")); };
  const clear = () => { ref.current.getContext("2d").clearRect(0, 0, ref.current.width, ref.current.height); onChange(null); };
  return (
    <div className="space-y-2">
      <canvas ref={ref} width={560} height={160} data-testid="sign-pad" className="w-full max-w-[560px] h-40 bg-white rounded-xl border-2 border-dashed border-slate-300 touch-none cursor-crosshair"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end} onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
      <button type="button" data-testid="sign-clear" onClick={clear} className="text-xs text-slate-500 flex items-center gap-1 hover:text-slate-800"><Eraser className="w-3.5 h-3.5" /> Clear signature</button>
    </div>
  );
}

export default function SignOffer() {
  const { token } = useParams();
  const [o, setO] = useState(null);
  const [err, setErr] = useState("");
  const [name, setName] = useState("");
  const [note, setNote] = useState("");
  const [reason, setReason] = useState("");
  const [declining, setDeclining] = useState(false);
  const [sig, setSig] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState({});

  useEffect(() => { api.get(`/sign/${token}`).then((r) => { setO(r.data); setName(r.data.candidate_name); }).catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail))); }, [token]);

  const submit = async (decision) => {
    if (!name.trim()) return toast.error("Please type your full legal name");
    if (decision === "accepted" && !sig) return toast.error("Please draw your signature to accept");
    if (decision === "declined" && !reason) return toast.error("Please choose a reason so the team can learn from it");
    setBusy(true);
    try { const { data } = await api.post(`/sign/${token}`, { name, decision, signature: sig, note, reason }); setResult(data); setO((p) => ({ ...p, status: decision, signed: { name, at: new Date().toISOString() } })); toast.success(decision === "accepted" ? "Offer accepted — congratulations!" : "Response recorded"); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); } finally { setBusy(false); }
  };

  if (err) return <div className="min-h-screen grid place-items-center bg-slate-100 text-slate-600 p-6 text-center" data-testid="sign-error">{err}</div>;
  if (!o) return <div className="min-h-screen grid place-items-center bg-slate-100 text-slate-500">Loading your offer…</div>;
  const done = ["accepted", "declined"].includes(o.status);
  const accent = o.company.color || "#2563EB";

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 py-8 px-4" data-testid="sign-page">
      <div className="max-w-3xl mx-auto space-y-5">
        <OfferPaper>
          <OfferLetterhead company={o.company} showLogo={o.show_logo !== false} logoOverride={o.letterhead_logo} offerDate={o.offer_date} candidate={{ name: o.candidate_name }} status={o.status} />
          <div data-testid="sign-letter" className="mt-8" dangerouslySetInnerHTML={{ __html: o.html }} />
        </OfferPaper>

        {done ? (
          <div data-testid="sign-done" className={`rounded-2xl p-6 text-center border ${o.status === "accepted" ? "bg-emerald-50 border-emerald-200 text-emerald-800" : "bg-rose-50 border-rose-200 text-rose-800"}`}>
            {o.status === "accepted" ? <CheckCircle2 className="w-10 h-10 mx-auto mb-2" /> : <XCircle className="w-10 h-10 mx-auto mb-2" />}
            <div className="font-bold text-lg">Offer {o.status}</div>
          {result.onboarding_link && <a data-testid="sign-onboarding-link" href={result.onboarding_link} className="mt-4 inline-block px-5 py-2.5 rounded-lg text-white font-semibold" style={{ background: accent }}>Continue to your onboarding checklist →</a>}
            <div className="text-sm">Recorded for {o.signed?.name} on {fmtDateTime(o.signed?.at)}. {o.status === "accepted" && "We look forward to welcoming you!"}</div>
          </div>
        ) : (
          <section className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6 sm:p-8 space-y-4" data-testid="sign-form">
            <h2 className="font-bold text-lg">Respond to this offer</h2>
            <div className="space-y-1"><Label>Type your full legal name</Label><Input data-testid="sign-name" value={name} onChange={(e) => setName(e.target.value)} /></div>
            <div className="space-y-1"><Label>Draw your signature</Label><SignaturePad onChange={setSig} /></div>
            <div className="space-y-1"><Label>Message (optional)</Label><Textarea data-testid="sign-note" rows={2} value={note} onChange={(e) => setNote(e.target.value)} /></div>
            <p className="text-xs text-slate-500">By clicking Accept, you agree that your typed name and drawn signature constitute your electronic signature and acceptance of this offer. Your response time and IP address are recorded.</p>
            <div className="flex gap-3 flex-wrap">
              <Button data-testid="sign-accept" disabled={busy} style={{ background: accent }} onClick={() => submit("accepted")} className="flex-1 h-12 text-base"><CheckCircle2 className="w-5 h-5 mr-2" /> Accept & Sign</Button>
              <Button data-testid="sign-decline" disabled={busy} variant="outline" onClick={() => setDeclining((v) => !v)} className="h-12"><XCircle className="w-5 h-5 mr-2" /> Decline</Button>
            </div>
            {declining && (
              <div className="rounded-xl border border-rose-200 bg-rose-50/50 p-4 space-y-3" data-testid="decline-panel">
                <Label className="text-sm">Sorry to hear that — what's the main reason?</Label>
                <div className="flex flex-wrap gap-2">
                  {["Compensation", "Start date", "Accepted another offer", "Role / responsibilities", "Location / commute", "Personal", "Other"].map((r) => (
                    <button type="button" key={r} data-testid={`decline-reason-${r.split(" ")[0].toLowerCase()}`} onClick={() => setReason(r)} className={`px-3 py-1.5 rounded-lg text-xs font-medium border ${reason === r ? "bg-rose-600 text-white border-transparent" : "border-slate-300 bg-white hover:bg-slate-50"}`}>{r}</button>
                  ))}
                </div>
                <Button data-testid="decline-confirm" disabled={busy || !reason} variant="destructive" onClick={() => submit("declined")} className="h-11">Confirm decline</Button>
              </div>
            )}
          </section>
        )}
        <div className="text-center text-xs text-slate-400 flex items-center justify-center gap-1.5">Powered by <img src="/rightpay-logo.png" alt="RightPay" className="h-4" /></div>
      </div>
    </div>
  );
}
